import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { API_BASE_URL } from '../../utils/constants';
import { FaSearch, FaCheckCircle, FaCircle, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './ManageSpecializationsScreen.css';

const ManageSpecializationsScreen = () => {
  const { user, setUser } = useUser();
  const [servicesList, setServicesList] = useState([]);
  const [selectedServices, setSelectedServices] = useState(user?.speciality || []);
  const [otherService, setOtherService] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const accessToken = localStorage.getItem('ACCESS_TOKEN');
      const response = await fetch(`${API_BASE_URL}api/doctor/specialities`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const allServiceList = data?.specialities || [];
        const categorizedServices = allServiceList.reduce((categories, service) => {
          const { type, _id, name } = service;
          if (!categories[type]) {
            categories[type] = [];
          }
          categories[type].push({ id: _id, name });
          return categories;
        }, {});

        const formattedServicesList = Object.keys(categorizedServices).map((category) => ({
          category,
          services: categorizedServices[category],
        }));

        setServicesList(formattedServicesList);
      } else {
        throw new Error('Failed to fetch services');
      }
    } catch (error) {
      toast.error('Failed to fetch services. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (serviceId) => {
    const isSelected = selectedServices.includes(serviceId);
    if (isSelected) {
      setSelectedServices(selectedServices.filter((s) => s !== serviceId));
    } else {
      setSelectedServices([...selectedServices, serviceId]);
    }
  };

  const handleSave = async () => {
    let finalServices = [...selectedServices];
    
    if (otherService.trim()) {
      finalServices.push({ id: 'other', name: otherService.trim() });
    }

    try {
      const accessToken = localStorage.getItem('ACCESS_TOKEN');
      const response = await fetch(`${API_BASE_URL}api/doctor/profile/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          speciality: finalServices,
        }),
      });

      if (response.ok) {
        setUser({
          ...user,
          speciality: finalServices,
        });
        toast.success('Your specializations have been updated successfully!');
      } else {
        throw new Error('Failed to save services');
      }
    } catch (error) {
      console.error('Error saving services:', error);
      toast.error('Failed to update specializations. Please try again.');
    }
  };

  const filteredServices = (categoryServices) =>
    categoryServices.filter((service) =>
      service?.name.toLowerCase().includes(searchQuery?.toLowerCase())
    );

  const isServiceSelected = (serviceId) => selectedServices.includes(serviceId);

  if (loading) {
    return (
      <div className="specializations-loading">
        <div className="loading-spinner"></div>
        <p>Loading specializations...</p>
      </div>
    );
  }

  return (
    <DashboardLayout title="Manage Specializations" showBackButton={true}>
      <div className="specializations-container">
        <div className="specializations-header">
          <h1>Manage Your Specializations</h1>
          <p>Select the services you provide as an expert.</p>
        </div>

        <div className="search-section">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="selected-count">
            Selected Services: <span className="count">{selectedServices.length}</span>
          </div>
        </div>

        <div className="services-grid">
          {servicesList.flatMap(category => 
            filteredServices(category.services).map((service, idx) => (
              <div
                key={`${category.category}-${idx}`}
                className={`service-item ${
                  isServiceSelected(service.id) ? 'selected' : ''
                }`}
                onClick={() => toggleService(service.id)}
              >
                <div className="service-checkbox">
                  {isServiceSelected(service.id) ? (
                    <FaCheckCircle className="checkbox-icon selected" />
                  ) : (
                    <FaCircle className="checkbox-icon" />
                  )}
                </div>
                <span className="service-name">{service.name}</span>
              </div>
            ))
          )}
        </div>

        {selectedServices.includes('other') && (
          <div className="other-service-section">
            <label className="other-label">Specify Other Service:</label>
            <input
              type="text"
              placeholder="Please specify..."
              value={otherService}
              onChange={(e) => setOtherService(e.target.value)}
              className="other-input"
            />
          </div>
        )}

        <button onClick={handleSave} className="save-button">
          <FaSave />
          Save Specializations
        </button>
      </div>
    </DashboardLayout>
  );
};

export default ManageSpecializationsScreen;